import React, { useState } from 'react';
import { CONSTANTS } from '../../constants';

const CreditCardValidator = ({ onClose }) => {
  const [creditCardNumber, setCreditCardNumber] = useState('');
  const [creditCardType, setCreditCardType] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const validateCreditCard = () => {
    const result = validateCreditCardNumber(creditCardNumber, creditCardType);
    setIsValid(result);
    setErrorMessage('');
  };

  const validateCreditCardNumber = (creditCardNumber, creditCardName) => {
    let cardType = -1;

    // Match up the selected Card Name
    for (let i = 0; i < CONSTANTS.CC.CARD_TYPES.length; i++) {
      if (creditCardName.toLowerCase() === CONSTANTS.CC.CARD_TYPES[i].name.toLowerCase()) {
        cardType = i;
        break;
      }
    }

    // If card type not found, report an error
    if (cardType === -1) {
      setErrorMessage(CONSTANTS.CC.ERRORS[0]);
      return false;
    }

    // Ensure that the user has provided a credit card number
    if (creditCardNumber.length === 0) {
      setErrorMessage(CONSTANTS.CC.ERRORS[1]);
      return false;
    }

    // Now remove any spaces from the credit card number
    creditCardNumber = creditCardNumber.replace(/\s/g, "");

    // Check that the number is numeric
    let cardNo = creditCardNumber;
    let cardRegEx = /^[0-9]{13,19}$/;
    if (!cardRegEx.exec(cardNo)) {
      setErrorMessage(CONSTANTS.CC.ERRORS[2]);
      return false;
    }

    // Now check the modulus 10 check digit - if required
    if (CONSTANTS.CC.CARD_TYPES[cardType].checkDigit) {
      let checksum = 0;
      let j = 1;

      // Process each digit one by one starting at the right
      let calc;
      for (let i = cardNo.length - 1; i >= 0; i--) {
        // Extract the next digit and multiply by 1 or 2 on alternative digits.
        calc = Number(cardNo.charAt(i)) * j;

        // If the result is in two digits add 1 to the checksum total
        if (calc > 9) {
          checksum = checksum + 1;
          calc = calc - 10;
        }

        // Add the units element to the checksum total
        checksum = checksum + calc;

        // Switch the value of j
        if (j === 1) {
          j = 2;
        } else {
          j = 1;
        }
      }

      // All done - if checksum is divisible by 10, it is a valid modulus 10.
      // If not, report an error.
      if (checksum % 10 !== 0) {
        setErrorMessage(CONSTANTS.CC.ERRORS[3]);
        return false;
      }
    }

    // Check it's not a spam number
    if (cardNo === '5490997771092064') {
      setErrorMessage(CONSTANTS.CC.ERRORS[5]);
      return false;
    }

    // The following are the card-specific checks we undertake.
    let LengthValid = false;
    let PrefixValid = false;

    // Load an array with the valid prefixes for this card
    let prefix = CONSTANTS.CC.CARD_TYPES[cardType].prefixes.split(",");

    // Now see if any of them match what we have in the card number
    for (let i = 0; i < prefix.length; i++) {
      let exp = new RegExp("^" + prefix[i]);
      if (exp.test(cardNo)) PrefixValid = true;
    }

    // If it isn't a valid prefix there's no point at looking at the length
    if (!PrefixValid) {
      setErrorMessage(CONSTANTS.CC.ERRORS[3]);
      return false;
    }

    // See if the length is valid for this card
    let lengths = CONSTANTS.CC.CARD_TYPES[cardType].length.split(",");
    for (let j = 0; j < lengths.length; j++) {
      if (cardNo.length === parseInt(lengths[j])) LengthValid = true;
    }

    // See if all is OK by seeing if the length was valid
    if (!LengthValid) {
      setErrorMessage(CONSTANTS.CC.ERRORS[4]);
      return false;
    }

    // The credit card is in the required format.
    return true;
  };

  const resetForm = () => {
    setCreditCardNumber('');
    setCreditCardType('');
    setIsValid(null);
    setErrorMessage('');
  };

  const formatCardNumber = (value) => {
    // Remove all non-digits
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCreditCardNumber(formatted);
  };

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="cardType" className="form-label">Card Type:</label>
        <select
          className="form-select"
          id="cardType"
          value={creditCardType}
          onChange={(e) => setCreditCardType(e.target.value)}
        >
          <option value="">Select Card Type</option>
          {CONSTANTS.CC.CARD_TYPES.map((card, index) => (
            <option key={index} value={card.name}>
              {card.uiName}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="cardNumber" className="form-label">Card Number:</label>
        <input
          type="text"
          className="form-control"
          id="cardNumber"
          value={creditCardNumber}
          onChange={handleCardNumberChange}
          placeholder="Enter card number (spaces will be added automatically)"
          maxLength={19}
        />
      </div>

      <div className="mb-3">
        <button 
          className="btn btn-primary me-2"
          onClick={validateCreditCard}
          disabled={!creditCardNumber.trim() || !creditCardType}
        >
          Validate Credit Card
        </button>
        
        <button 
          className="btn btn-secondary"
          onClick={resetForm}
        >
          Reset
        </button>
      </div>

      {isValid !== null && (
        <div className="mt-4">
          {isValid ? (
            <div className="alert alert-success">
              <h5>✓ Valid Credit Card!</h5>
              <p>This credit card number is valid for the selected card type.</p>
            </div>
          ) : (
            <div className="alert alert-danger">
              <h5>✗ Invalid Credit Card</h5>
              <p>{errorMessage}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        <h6>Instructions:</h6>
        <ol>
          <li>Select the credit card type from the dropdown</li>
          <li>Enter the credit card number (spaces will be added automatically)</li>
          <li>Click "Validate Credit Card" to check if it's valid</li>
          <li>The validator checks format, length, prefix, and Luhn algorithm</li>
        </ol>
        
        <div className="alert alert-info mt-3">
          <strong>Note:</strong> This is for demonstration purposes only. 
          Never enter real credit card information on public websites.
        </div>
      </div>
    </div>
  );
};

export default CreditCardValidator;
